// JWT签名工具
// 注意：这里使用浏览器端的JWT库，实际签名会在服务端验证

/**
 * 创建用户信息的JWT签名
 * @param userInfo 用户信息
 * @param jwtSecret JWT密钥（从环境变量或配置获取）
 * @returns 签名的JWT token
 */
export const signUserInfo = async (userInfo: {
  userId: string;
  username?: string;
  email?: string;
  [key: string]: any;
}, jwtSecret: string): Promise<string> => {
  // 构建JWT payload
  const header = {
    alg: 'HS256',
    typ: 'JWT',
  };

  const payload = {
    ...userInfo,
    iat: Math.floor(Date.now() / 1000), // 签发时间
    exp: Math.floor(Date.now() / 1000) + 3600, // 1小时过期
  };

  // 使用Web Crypto API进行HMAC-SHA256签名
  const encoder = new TextEncoder();
  
  // Base64 URL编码（JWT标准）。btoa 只支持 Latin1，需先将 UTF-8 转为字节再编码
  const base64UrlEncodeStr = (str: string): string => {
    const bytes = encoder.encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };
  // 对二进制字节直接 base64url，不经过 UTF-8（否则会破坏 128-255 的字节）
  const base64UrlEncodeBytes = (bytes: Uint8Array): string => {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const headerBase64 = base64UrlEncodeStr(JSON.stringify(header));
  const payloadBase64 = base64UrlEncodeStr(JSON.stringify(payload));

  const message = `${headerBase64}.${payloadBase64}`;

  // 确保密钥长度至少32字节（256位），如果不够则填充
  let keyData = encoder.encode(jwtSecret);
  if (keyData.length < 32) {
    // 如果密钥太短，使用SHA-256哈希来扩展
    const hashBuffer = await crypto.subtle.digest('SHA-256', keyData);
    keyData = new Uint8Array(hashBuffer);
  } else if (keyData.length > 32) {
    // 如果密钥太长，截取前32字节
    keyData = keyData.slice(0, 32);
  }

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message));
  const signatureArray = new Uint8Array(signature);
  const signatureBase64 = base64UrlEncodeBytes(signatureArray);
  
  return `${message}.${signatureBase64}`;
};

/**
 * 从 session 中提取用户信息
 * 入参需与 Kratos GET /sessions/whoami 返回结构一致（见 auth.ts Session）
 * 使用 identity.id 作为 userId，勿用顶层 session.id（为 session 自身 id）
 */
export const extractUserInfoFromSession = (session: {
  identity: {
    id: string;
    traits?: {
      email?: string;
      username?: string;
      [key: string]: any;
    };
  };
}): {
  userId: string;
  username?: string;
  email?: string;
  [key: string]: any;
} => {
  const identity = session.identity;
  const traits = identity.traits ?? {};
  const userId = identity.id;
  if (!userId) {
    throw new Error('会话中缺少用户标识(identity.id)');
  }
  return {
    userId,
    username: traits.username ?? traits.email,
    email: traits.email,
    ...traits,
  };
};
